/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom"; // ⭐ Params vanga idhu mukkiyam
import { Row, Col, Card, CardBody, Table, Button, Badge, Spinner, Progress } from "reactstrap";
import Handler from "../../Helpers/Handler";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ArrowLeft, Printer, briefcase, DollarSign, Clock } from "lucide-react";

const DriverJobReport = () => {
    const { id } = useParams(); // ⭐ URL path "/report/:id" la irundhu ID edukurom
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
    const [toDate, setToDate] = useState(dayjs());
    const [loading, setLoading] = useState(false);

    // ⭐ Fetch logic wrapped in useCallback to prevent re-renders
    const fetchReport = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const url = `/driver/salary_report.php?driver_id=${id}&from_date=${fromDate.format('YYYY-MM-DD')}&to_date=${toDate.format('YYYY-MM-DD')}`;
            const res = await Handler({ method: "get", url });

            if (res.success) {
                setReport(res.data);
            }
        } catch (error) {
            console.error("Report Error:", error);
        } finally {
            setLoading(false);
        }
    }, [id, fromDate, toDate]);

    // ⭐ Auto load when ID or Dates change
    useEffect(() => {
        fetchReport();
    }, [id]);

    return (
        <div className="p-4 min-vh-100" style={{ backgroundColor: "#F8FAFC", fontFamily: 'Plus Jakarta Sans, sans-serif' }}>

            {/* Header Area */}
            <div className="d-flex justify-content-between align-items-center mb-4 no-print">
                <div className="d-flex align-items-center gap-3">
                    <Button color="white" className="border shadow-sm rounded-circle p-2" onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h3 className="fw-800 text-slate-900 mb-0">Settlement Intelligence</h3>
                        <p className="text-slate-500 fw-500 mb-0">Driver: {report?.driver_name || "Loading..."}</p>
                    </div>
                </div>
                <Button color="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={() => window.print()}>
                    <Printer size={18} className="me-2" /> PRINT REPORT
                </Button>
            </div>

            {/* Filter Bar */}
            <Card className="border-0 shadow-sm rounded-4 mb-5 overflow-hidden no-print">
                <CardBody className="p-4 bg-white">
                    <Row className="align-items-end g-3">
                        <Col lg={9}>
                            <label className="small fw-bold text-slate-400 uppercase ls-1 d-block mb-2">Reporting Period</label>
                            <div className="d-flex align-items-center gap-2">
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker value={fromDate} onChange={setFromDate} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                    <span className="text-slate-300 px-2">to</span>
                                    <DatePicker value={toDate} onChange={setToDate} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                </LocalizationProvider>
                            </div>
                        </Col>
                        <Col lg={3}>
                            <Button color="dark" className="w-100 rounded-3 py-2 fw-bold text-white shadow-sm" onClick={fetchReport} disabled={loading}>
                                {loading ? <Spinner size="sm" /> : "REFRESH DATA"}
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading && !report ? (
                <div className="text-center p-5"><Spinner color="primary" /></div>
            ) : report && (
                <>
                    {/* Visual Analytics */}
                    <Row className="mb-5 g-4">
                        <Col lg={4}>
                            <div className="p-4 rounded-4 text-white shadow-lg d-flex flex-column h-100" style={{ background: 'linear-gradient(135deg, #4318FF 0%, #5E35B1 100%)' }}>
                                <span className="opacity-75 small fw-bold uppercase mb-2 ls-1">Estimated Payout</span>
                                <h1 className="fw-800 mb-0" style={{ fontSize: '3rem' }}>${parseFloat(report.summary.grand_total_salary).toLocaleString()}</h1>
                                <div className="mt-auto pt-3 border-top border-white border-opacity-20 mt-4">
                                    <Badge color="light" className="text-primary me-2">{report.summary.total_jobs} TRIPS</Badge>
                                    <small className="fw-bold uppercase ls-1">Verified Logistics Data</small>
                                </div>
                            </div>
                        </Col>

                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
                                <CardBody className="p-4">
                                    <h6 className="fw-800 text-slate-800 mb-4 uppercase ls-1">Operational Hours breakdown</h6>
                                    <Row className="text-center g-3">
                                        {[
                                            { label: 'Normal Off', val: report.summary.hours_breakdown.office, color: 'primary' },
                                            { label: 'Normal OT', val: report.summary.hours_breakdown.office_ot, color: 'danger' },
                                            { label: 'After Office', val: report.summary.hours_breakdown.after_office_ot, color: 'warning' },
                                            { label: 'Holiday OT', val: report.summary.hours_breakdown.holiday_ot, color: 'success' }
                                        ].map((hr, i) => (
                                            <Col xs={6} md={3} key={i}>
                                                <div className={`p-3 rounded-4 bg-${hr.color} bg-opacity-10 mb-2 border border-${hr.color} border-opacity-10`}>
                                                    <h4 className={`fw-800 text-${hr.color} mb-0`}>{hr.val}h</h4>
                                                </div>
                                                <small className="text-slate-500 fw-bold uppercase" style={{ fontSize: '10px' }}>{hr.label}</small>
                                            </Col>
                                        ))}
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Job Ledger Table */}
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden border">
                        <div className="p-4 bg-white border-bottom d-flex justify-content-between align-items-center">
                            <h6 className="fw-800 mb-0 text-slate-900 uppercase ls-1">Activity Log Ledger</h6>
                            <Badge color="success-soft" className="text-success border border-success border-opacity-10 px-3 py-2">Account Ready</Badge>
                        </div>
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-slate-50 text-slate-400 small uppercase ls-1">
                                <tr>
                                    <th className="ps-4 py-3">Trip Date</th>
                                    <th>Route (From → To)</th>
                                    <th>Work Window</th>
                                    <th>Hour Breakdown</th>
                                    <th className="text-end pe-4">Salary</th>
                                </tr>
                            </thead>
                            <tbody className="small">
                                {report.logs.map((log, idx) => (
                                    <tr key={idx} className="border-bottom border-light">
                                        <td className="ps-4 py-3">
                                            <div className="fw-bold text-slate-800">{dayjs(log.job_date).format('DD MMM YYYY')}</div>
                                            <div className="text-indigo-600 fw-600 uppercase" style={{ fontSize: '10px' }}>{log.lorry_type}</div>
                                        </td>
                                        <td>
                                            <div className="fw-600 text-slate-700">{log.pickup_location}</div>
                                            <div className="text-slate-400 extra-small">→ {log.delivery_location}</div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2 text-dark fw-bold font-monospace bg-light rounded px-2 py-1" style={{ width: 'fit-content' }}>
                                                {log.formatted_start} - {log.formatted_end}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {parseFloat(log.office_hrs) > 0 && <Badge color="indigo-soft" className="text-primary border">OFF: {log.office_hrs}h</Badge>}
                                                {parseFloat(log.office_hrs_ot) > 0 && <Badge color="danger-soft" className="text-danger border">OT: {log.office_hrs_ot}h</Badge>}
                                                {parseFloat(log.after_office_hrs_ot) > 0 && <Badge color="warning-soft" className="text-warning border">AFT: {log.after_office_hrs_ot}h</Badge>}
                                                {parseFloat(log.holiday_ot) > 0 && <Badge color="success-soft" className="text-success border">HOL: {log.holiday_ot}h</Badge>}
                                            </div>
                                        </td>
                                        <td className="text-end pe-4 fw-800 text-slate-900" style={{ fontSize: '15px' }}>
                                            ${parseFloat(log.salary_amount).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .fw-800 { font-weight: 800; }
                .ls-1 { letter-spacing: 0.05em; }
                .indigo-soft { background-color: #EEF2FF; }
                .danger-soft { background-color: #FEF2F2; }
                .warning-soft { background-color: #FFFBEB; }
                .success-soft { background-color: #F0FDF4; }
                .extra-small { font-size: 10px; }
                @media print {
                    .no-print, .btn, .card:first-child { display: none !important; }
                    .p-4 { padding: 0 !important; }
                    .shadow-lg, .shadow-sm { box-shadow: none !important; border: 1px solid #eee !important; }
                    body { background-color: white !important; }
                }
            `}</style>
        </div>
    );
};

export default DriverJobReport;